/**
 * Fetches roles and departments (LDR — Location/Department/Role) so the
 * `ldr_cache` IndexedDB store (API-4378) can be populated for offline
 * role/department selection (API-4649).
 *
 * Mirrors iOS's `AppDelegateCacheFetcher`: up to `MAX_RETRIES` retries per
 * resource, cursor-based pagination, and a client-side join of each role's
 * `department_id` onto the department list — the roles endpoint does not
 * return a department name directly.
 */

import { env } from '@src/config/env';
import { fetchWithNetworkReporting } from './accountApi';
import { normalizeColor } from './TimePunchClockApi';
import type { ClockRole, ClockDepartment } from './punchTypes';

const MAX_RETRIES = 2;
const PAGE_LIMIT = 500;

interface ApiDepartment {
    id: number;
    name: string;
}

interface ApiRole {
    id: number;
    department_id: number | null;
    color: string | null;
    name: string;
}

interface PaginatedResponse<T> {
    data: T[];
    meta?: {
        cursor?: {
            next?: string | null;
        };
    };
}

async function fetchAllPages<T>(
    buildUrl: (cursor: string | null) => string,
    accessToken: string,
    resourceName: string
): Promise<T[]> {
    const results: T[] = [];
    let cursor: string | null = null;

    do {
        const res = await fetchWithNetworkReporting(buildUrl(cursor), {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json'
            }
        });

        if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(
                `${resourceName} fetch failed (${res.status}): ${body || res.statusText}`
            );
        }

        const json: PaginatedResponse<T> = await res.json();
        results.push(...(json.data ?? []));
        cursor = json.meta?.cursor?.next ?? null;
    } while (cursor);

    return results;
}

async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            return await fn();
        } catch (err) {
            lastErr = err;
        }
    }
    throw lastErr;
}

async function fetchApiDepartments(
    companyId: number,
    locationId: number,
    accessToken: string
): Promise<ApiDepartment[]> {
    return withRetry(() =>
        fetchAllPages<ApiDepartment>(
            cursor => {
                const url = new URL(
                    `${env.apiBaseUrl}/api/v2/company/${companyId}/departments`
                );
                url.searchParams.set('location_id', String(locationId));
                url.searchParams.set('limit', String(PAGE_LIMIT));
                if (cursor) url.searchParams.set('cursor', cursor);
                return url.toString();
            },
            accessToken,
            'Departments'
        )
    );
}

async function fetchApiRoles(
    companyId: number,
    locationId: number,
    accessToken: string
): Promise<ApiRole[]> {
    return withRetry(() =>
        fetchAllPages<ApiRole>(
            cursor => {
                const url = new URL(
                    `${env.apiBaseUrl}/api/v2/company/${companyId}/roles`
                );
                url.searchParams.set('location_id', String(locationId));
                url.searchParams.set('limit', String(PAGE_LIMIT));
                if (cursor) url.searchParams.set('cursor', cursor);
                return url.toString();
            },
            accessToken,
            'Roles'
        )
    );
}

export interface LDRFetchResult {
    departments: ClockDepartment[];
    roles: ClockRole[];
}

/**
 * Fetches departments and roles for a location and joins each role's
 * `departmentId` to its department name (the roles endpoint only returns
 * `department_id`, not a name).
 */
export async function fetchLDR(
    companyId: number,
    locationId: number,
    accessToken: string
): Promise<LDRFetchResult> {
    const [apiDepartments, apiRoles] = await Promise.all([
        fetchApiDepartments(companyId, locationId, accessToken),
        fetchApiRoles(companyId, locationId, accessToken)
    ]);

    const departments: ClockDepartment[] = apiDepartments.map(d => ({
        id: d.id,
        name: d.name
    }));

    const departmentNameById = new Map(departments.map(d => [d.id, d.name]));

    const roles: ClockRole[] = apiRoles.map(r => ({
        id: r.id,
        name: r.name,
        departmentId: r.department_id,
        departmentName:
            r.department_id !== null
                ? (departmentNameById.get(r.department_id) ?? null)
                : null,
        color: normalizeColor(r.color)
    }));

    return { departments, roles };
}
